import { useEffect, useId, useState, useRef } from 'react';
import { Sparkles, Check } from 'lucide-react';
import { accountsEnabled, requestWithAdultRefresh } from './AdultAuthPanel';
import './FamilyPremiumSection.css';

// Presentation only. Billing must resolve products/prices/authority on the server.
const { resolveSandboxCheckout, resolveLiveCheckout } = require('../accountsEnvironment');
const sandboxCheckout = resolveSandboxCheckout(process.env);
const liveCheckout = resolveLiveCheckout(process.env);
const checkoutEnabled = sandboxCheckout || liveCheckout;
const PLANS = [
  { id: 'monthly', name: 'Mensual', price: '$4.99', period: 'al mes' },
  { id: 'annual', name: 'Anual', price: '$39.99', period: 'al año' },
];
export default function FamilyPremiumSection({ familyId, student }) {
  const id = useId(), binding = `${familyId}:${student.student_id}`;
  const [access, setAccess] = useState(null);
  const [revision, setRevision] = useState(0);
  const [choice, setChoice] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const flight = useRef(null);
  const approvalLink = useRef(null);
  const checkoutButton = useRef(null);
  useEffect(() => { flight.current = null; return () => { flight.current = null; }; }, [binding]);
  async function startCheckout() {
    if (!checkoutEnabled || flight.current) return;
    const token = {}; flight.current = token;
    setCheckout({ binding, state: 'pending' });
    try {
      const code = choice?.binding === binding && choice.plan === 'annual'
        ? 'ARITHMETIC_ADVENTURE_ANNUAL_V1' : 'ARITHMETIC_ADVENTURE_MONTHLY_V1';
      const result = await requestWithAdultRefresh(`/families/${familyId}/students/${student.student_id}/billing/checkout`,
        { method: 'POST', body: JSON.stringify({ plan_code: code }) });
      if (flight.current !== token) return;
      const url = new URL(result.approval_url);
      if (result.status !== 'PENDING' || url.origin !== (liveCheckout ? 'https://www.paypal.com' : 'https://www.sandbox.paypal.com') ||
          url.pathname !== '/webapps/billing/subscriptions' || url.hash || url.username || url.password) throw new Error();
      // The checkout may be created asynchronously. Never navigate the current
      // TechnoMathPR tab or attempt an async popup that browsers may block.
      setCheckout({ binding, state: 'ready', approvalUrl: url.href });
    } catch {
      if (flight.current === token) setCheckout({ binding, state: 'error' });
    } finally { if (flight.current === token) flight.current = null; }
  }
  useEffect(() => {
    if (checkout?.binding === binding && checkout.state === 'ready') approvalLink.current?.focus();
  }, [binding, checkout]);
  function dismissCheckout() {
    setCheckout(null);
    checkoutButton.current?.focus();
  }
  useEffect(() => {
    if (!accountsEnabled) return undefined;
    let active = true;
    setAccess({ binding, state: 'loading' });
    requestWithAdultRefresh(`/families/${familyId}/students/${student.student_id}/content-access`)
      .then(result => {
        if (result?.student_id !== student.student_id || result.product_code !== 'ARITHMETIC_ADVENTURE' || typeof result.premium !== 'boolean') {
          throw new Error('Invalid access response');
        }
        if (active) setAccess({ binding, state: 'ready', premium: result.premium });
      }).catch(() => { if (active) setAccess({ binding, state: 'unavailable' }); });
    return () => { active = false; };
  }, [binding, familyId, student.student_id, revision]);
  useEffect(() => {
    if (!accountsEnabled) return undefined;
    const refresh = () => { if (document.visibilityState !== 'hidden') setRevision(value => value + 1); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);
  if (!accountsEnabled) return null;
  const current = access?.binding === binding ? access : { state: 'loading' };
  const selected = choice?.binding === binding ? choice.plan : 'monthly';
  return <section className="tmag-family-premium" aria-labelledby={`${id}-title`}>
    <header className="tmag-family-premium__heading"><Sparkles aria-hidden="true" /><div>
      <p className="tmag-family-premium__eyebrow">UN UNIVERSO POR DESCUBRIR</p>
      <h4 id={`${id}-title`}>Premium para {student.alias}</h4>
    </div></header>
    <div className="tmag-family-premium__access" aria-live="polite">
      {current.state === 'loading' && <p role="status">Consultando su acceso…</p>}
      {current.state === 'ready' && <p className={`tmag-family-premium__badge ${current.premium ? 'is-premium' : ''}`}>
        {current.premium ? 'Acceso Premium vigente' : 'Acceso gratuito verificado'}
      </p>}
      {current.state === 'unavailable' && <p role="alert">Estado no disponible. No pudimos verificar su acceso. Revisa tu sesión adulta y vuelve a intentarlo.</p>}
      <button type="button" className="tmag-family-premium__refresh" disabled={current.state === 'loading'} onClick={() => setRevision(value => value + 1)}>
        {current.state === 'unavailable' ? 'Reintentar acceso' : 'Actualizar acceso'}
      </button>
    </div>
    <p>La primera misión de Suma, Resta, Multiplicación y División sigue siendo gratuita.</p>
    <ul className="tmag-family-premium__benefits">
      <li><Check aria-hidden="true" /><span>Premium incluye las otras <strong>24 misiones</strong> de los cuatro mundos.</span></li>
      <li><Check aria-hidden="true" /><span>Aventura, Práctica y práctica inteligente. Se mantienen los requisitos de aprendizaje.</span></li>
      <li><Check aria-hidden="true" /><span>El progreso se conserva aunque termine el acceso Premium.</span></li>
    </ul>
    <fieldset className="tmag-family-premium__plans"><legend>Planes por estudiante</legend>
      <div>{PLANS.map(plan => <label className={`tmag-family-premium__plan ${selected === plan.id ? 'is-selected' : ''}`} key={plan.id}>
        <input type="radio" name={`${id}-plan`} value={plan.id} checked={selected === plan.id} disabled={checkout?.binding === binding && checkout.state === 'pending'} onChange={() => setChoice({ binding, plan: plan.id })} />
        <span><strong>{plan.name}</strong><span className="tmag-family-premium__price">{plan.price} <small>USD</small></span><span>{plan.period} · por estudiante</span></span>
      </label>)}</div>
    </fieldset>
    <p className="tmag-family-premium__individual">Cada niño tiene su propio acceso. Premium para uno no se comparte con sus hermanos.</p>
    {checkoutEnabled ? <aside className="tmag-family-premium__soon">
      <strong>{liveCheckout ? 'Suscripción Premium por estudiante' : 'Prueba PayPal Sandbox · dinero ficticio'}</strong>
      <p>{liveCheckout ? 'La aprobación en PayPal no activa Premium por sí sola. Verificaremos el cobro y el período pagado para este estudiante.' : 'La aprobación no confirma el cobro ni activa Premium. Solo usuarios de prueba autorizados.'}</p>
      <button ref={checkoutButton} type="button" disabled={checkout?.binding === binding && checkout.state === 'pending'} onClick={startCheckout}>
        {checkout?.binding === binding && checkout.state === 'pending' ? (liveCheckout ? 'Preparando suscripción…' : 'Preparando prueba…') : (liveCheckout ? 'Continuar a PayPal' : 'Continuar a PayPal Sandbox')}
      </button>
      {checkout?.binding === binding && checkout.state === 'error' && <p role="alert">No pudimos confirmar el inicio. Reintentar recupera el mismo contrato; no inicia otra contratación.</p>}
      {checkout?.binding === binding && checkout.state === 'ready' && <div className="tmag-family-premium__modal-overlay">
        <div className="tmag-family-premium__modal" role="dialog" aria-modal="true" aria-labelledby={`${id}-checkout-title`}
          onKeyDown={event => {
            if (event.key === 'Escape') { event.preventDefault(); dismissCheckout(); }
            if (event.key !== 'Tab') return;
            const items = [approvalLink.current, ...event.currentTarget.querySelectorAll('button:not([disabled])')].filter(Boolean);
            const first = items[0], last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          }}>
          <h5 id={`${id}-checkout-title`}>{liveCheckout ? 'Continuar con PayPal' : 'Continuar con PayPal Sandbox'}</h5>
          <p>{liveCheckout ? 'PayPal se abrirá en otra pestaña para revisar y aprobar la suscripción. TechnoMathPR permanecerá abierto.' : 'El pago ficticio se abrirá aparte. TechnoMathPR permanecerá abierto para que puedas regresar.'}</p>
          <p>{liveCheckout ? 'Cancelar esta ventana no cancela una suscripción que ya hayas aprobado en PayPal.' : 'Si no deseas seguir, selecciona Cancelar. Eso no aprueba ningún pago y conserva el contrato de prueba pendiente.'}</p>
          <div className="tmag-family-premium__modal-actions">
            <a ref={approvalLink} href={checkout.approvalUrl} target="_blank" rel="noopener noreferrer">Abrir PayPal en otra pestaña</a>
            <button type="button" onClick={dismissCheckout}>Cancelar y seguir en TechnoMathPR</button>
          </div>
        </div>
      </div>}
    </aside> : <aside className="tmag-family-premium__soon"><strong>Contratación disponible próximamente</strong>
      <p>Elegir un plan no activa Premium ni genera cargos. Los pagos todavía no están habilitados.</p>
    </aside>}
  </section>;
}


